import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import type { Extension } from '@codemirror/state';

/**
 * Editor chrome. Every colour is a CSS custom property, so a theme switch is a
 * repaint rather than a CodeMirror reconfiguration.
 */
const base = EditorView.theme({
  '&': {
    color: 'var(--c-ink)',
    backgroundColor: 'var(--c-base)',
    fontSize: '13px',
  },
  '.cm-content': {
    fontFamily: 'var(--f-mono)',
    padding: '8px 0',
    caretColor: 'var(--c-local)',
  },
  '.cm-line': { padding: '0 10px' },
  '.cm-gutters': {
    backgroundColor: 'var(--c-surface)',
    color: 'var(--c-faint)',
    border: 'none',
    borderRight: '1px solid var(--c-line)',
    fontFamily: 'var(--f-mono)',
    fontSize: '11px',
  },
  '.cm-activeLineGutter': { backgroundColor: 'var(--c-raised)', color: 'var(--c-muted)' },
  '.cm-activeLine': { backgroundColor: 'transparent' },
  '&.cm-focused .cm-activeLine': { backgroundColor: 'color-mix(in srgb, var(--c-raised) 55%, transparent)' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--c-local)', borderLeftWidth: '2px' },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
    backgroundColor: 'var(--c-selection)',
  },
  '.cm-selectionMatch': { backgroundColor: 'var(--c-local-dim)' },
  '.cm-tooltip': {
    backgroundColor: 'var(--c-surface)',
    border: '1px solid var(--c-line-strong)',
    borderRadius: 'var(--r-sm)',
    boxShadow: '0 8px 24px var(--c-shadow)',
    fontFamily: 'var(--f-ui)',
    fontSize: '12px',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul': {
    fontFamily: 'var(--f-mono)',
    fontSize: '12px',
    maxHeight: '18em',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li': { padding: '2px 8px' },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]': {
    backgroundColor: 'var(--c-selection)',
    color: 'var(--c-ink)',
  },
  '.cm-completionIcon': { display: 'none' },
  '.cm-completionDetail': {
    fontStyle: 'normal',
    color: 'var(--c-faint)',
    fontSize: '10px',
    marginLeft: '10px',
  },
  '.cm-lintRange-error': {
    // A dotted underline reads as "check this" rather than "broken".
    backgroundImage: 'none',
    borderBottom: '2px dotted var(--c-danger)',
  },
  '.cm-diagnostic-error': { borderLeft: '3px solid var(--c-danger)' },
  '.cm-panels': { backgroundColor: 'var(--c-surface)', color: 'var(--c-ink)' },
  '.cm-searchMatch': { backgroundColor: 'var(--c-remote-dim)' },
  '.cm-scroller': { fontFamily: 'var(--f-mono)', lineHeight: '1.55' },
});

const highlight = HighlightStyle.define([
  { tag: tags.keyword, color: 'var(--s-keyword)', fontWeight: '500' },
  { tag: tags.operatorKeyword, color: 'var(--s-keyword)' },
  { tag: [tags.string, tags.special(tags.string)], color: 'var(--s-string)' },
  { tag: [tags.number, tags.bool, tags.null], color: 'var(--s-number)' },
  { tag: tags.comment, color: 'var(--s-comment)', fontStyle: 'italic' },
  { tag: [tags.typeName, tags.standard(tags.typeName)], color: 'var(--s-type)' },
  { tag: tags.function(tags.variableName), color: 'var(--s-func)' },
  { tag: tags.variableName, color: 'var(--c-ink)' },
  { tag: tags.propertyName, color: 'var(--c-ink)' },
  { tag: tags.punctuation, color: 'var(--c-muted)' },
  { tag: tags.operator, color: 'var(--c-muted)' },
]);

export function workbenchTheme(): Extension {
  return [base, syntaxHighlighting(highlight)];
}
